import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, FileText } from "lucide-react";
import { subscribe, track } from "@/lib/api";

export default function Newsletter({ compact = false }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [dossierUrl, setDossierUrl] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribe({ email, name: name || undefined });
      if (res.dossier_url) setDossierUrl(res.dossier_url);
      if (res.status === "already_subscribed") {
        toast.info(res.message || "You're already on the list!");
      } else {
        toast.success(res.message || "Welcome aboard!");
        setEmail("");
        setName("");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className={`relative overflow-hidden grain ${compact ? "" : "border-y border-zinc-800"} bg-zinc-900/40`}
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-20 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs tracking-accent uppercase text-amber-500 mb-4 inline-flex items-center gap-2">
            <FileText size={14} /> Free Download
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Get the Luminaria Dossier
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-8 leading-relaxed">
            Join the reader list and instantly unlock the free, spoiler-free <span className="text-zinc-200">Luminaria Dossier</span>, your field guide to the world of The Luminaria Chronicles. Plus first word on new releases and cover reveals.
          </p>

          {dossierUrl ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              data-testid="dossier-download-block"
              className="max-w-lg mx-auto border border-amber-500/40 bg-zinc-900/60 p-6"
            >
              <p className="text-zinc-300 mb-5">You're in. Your Dossier is ready.</p>
              <a
                href={dossierUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="dossier-download-button"
                onClick={() => track("dossier_download", "/newsletter")}
                className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 font-bold text-sm tracking-wide px-7 py-4 hover:bg-amber-400 transition-colors"
              >
                <Download size={16} /> Download the Dossier
              </a>
            </motion.div>
          ) : (
            <form onSubmit={onSubmit} data-testid="newsletter-form" className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <input
                data-testid="newsletter-name-input"
                type="text"
                placeholder="First name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors"
              />
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors"
              />
              <button
                data-testid="newsletter-submit-button"
                type="submit"
                disabled={loading}
                className="bg-amber-500 text-zinc-950 font-bold text-sm tracking-wide px-6 py-3 hover:bg-amber-400 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {loading ? "Joining…" : "Get the Dossier"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
